import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Edit, Trash2, Bell, User, MessageSquareText, AlertCircle } from 'lucide-react';
import { Reminder } from '@common/models/reminder';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { DBReminderThunks } from '@common/reducers/DBServiceThunks';
import { openEditor } from '@common/reducers/RemindersUIReducer';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { selectMergedContactMap } from '@common-reducers/WhatsAppReducer';
import { getWaChatContactDisplayName } from '@common/utils/display-name-utils';
import { fetchAndCacheChatNameThunk, fetchAndCacheProfilePicUrlsThunk } from '@extension-context-content/thunks/WhatsappThunks';

interface ReminderItemProps {
    itemId: string;
    index?: number;
}

// Local text flattener — equivalent to slate's Node.string() for a Descendant
// (recursively concatenates every leaf's `.text`). Kept slate-free so the
// reminders LIST (a display hot-path, rendered without opening any editor) does
// not pin slate in app.js. See src/extension/utils/slate-runtime.ts / the R17-A
// slate lazy split.
const nodeString = (node: any): string => {
    if (node == null) return '';
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.children)) return node.children.map(nodeString).join('');
    return '';
};

const serialize = (nodes: any[]) => {
    return (nodes ?? []).map(nodeString).join('\n');
};

const ReminderItem: React.FC<ReminderItemProps> = ({ itemId, index }) => {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const activeLocale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase();
    
    const reminder = useAppSelector(state => 
        state.DBRemindersReducer.ids.includes(itemId) 
            ? state.DBRemindersReducer.entities[itemId] 
            : undefined
    ) as Reminder;

    const mergedContactMap = useAppSelector(selectMergedContactMap);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const relatedChatId = reminder?.relatedChatId;
    const contact = relatedChatId ? mergedContactMap[relatedChatId] : undefined;
    const contactExt = contact as { imgUrl?: string; profilePicLastUpdate?: string } | undefined;
    const hasCachedName = !!contact?.name;
    const hasCachedPic = !!contactExt?.imgUrl;
    const hasTriedPic = !!contactExt?.profilePicLastUpdate;
    useEffect(() => {
        if (!relatedChatId) return;
        if (!hasCachedName) dispatch(fetchAndCacheChatNameThunk(relatedChatId));
        // Skip when already cached OR when previous attempt produced nothing.
        if (!hasCachedPic && !hasTriedPic) dispatch(fetchAndCacheProfilePicUrlsThunk(relatedChatId));
    }, [dispatch, relatedChatId, hasCachedName, hasCachedPic, hasTriedPic]);

    if (!reminder) return null;

    const plainTextDescription = serialize(reminder.description || []);
    const resolvedFromMap = getWaChatContactDisplayName(contact, { fallback: '' });
    const isUnknownPlaceholder =
        !reminder.relatedChatDisplayName ||
        reminder.relatedChatDisplayName === 'Unknown Contact' ||
        reminder.relatedChatDisplayName === reminder.relatedChatId;
    const chatDisplayName = isUnknownPlaceholder
        ? (resolvedFromMap || reminder.relatedChatDisplayName || reminder.relatedChatId?.split('@')[0])
        : reminder.relatedChatDisplayName;

    const handleEdit = (reminder: Reminder) => {
        // Dispatch Redux action to open editor with existing reminder
        dispatch(openEditor({
            reminder,
            context: {
                messageId: reminder.relatedMessageId,
                messageText: reminder.relatedMessageText,
                chatId: reminder.relatedChatId,
                chatDisplayName: reminder.relatedChatDisplayName,
            }
        }));
    };

    const handleDelete = (id: string) => {
        if (id) {
             dispatch(DBReminderThunks.delete({ entity: { _id: id } as Reminder }));
        }
        setIsDeleteDialogOpen(false);
    };

    const handleToggleActive = (checked: boolean) => {
        dispatch(DBReminderThunks.patch({
            entity: { ...reminder, isActive: checked },
            options: { ignoreOriginalEntity: false }
        }));
    };

    // Determine Context Badge
    let contextBadge = null;
    if (chatDisplayName) {
        let Icon = User;
        let variant: "secondary" | "outline" | "default" | "destructive" = "secondary";

        if (reminder.relatedMessageId || reminder.relatedMessageText) {
            Icon = MessageSquareText;
        }

        contextBadge = (
            <Badge variant={variant} className="mt-1.5 mb-1 px-2 py-0.5 font-normal flex w-fit items-center gap-1.5 h-6">
                <Icon className="h-3 w-3" />
                <span>{chatDisplayName}</span>
            </Badge>
        );
    }

    return (
        <Card className="mb-4 rounded-lg shadow-sm border border-border hover:shadow-md transition-shadow overflow-hidden">
            <CardContent className="p-4">
                <div className="flex gap-3 min-w-0">
                    {/* Left side - Icon & Content */}
                    <div className="flex gap-3 flex-1 min-w-0">
                        <div className="text-muted-foreground/70 flex-shrink-0 mt-0.5">
                            <Bell className="h-5 w-5" />
                        </div>

                        <div className="flex flex-col flex-1 min-w-0">
                            <h4 className="font-semibold text-sm leading-tight mb-1.5 truncate">
                                {reminder.title}
                            </h4>
                            {contextBadge}
                            {plainTextDescription && (
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-1.5 break-words">
                                    {plainTextDescription}
                                </p>
                            )}
                            <span className="text-xs text-muted-foreground block mt-auto">
                                {t('reminders.due', 'Due')}: {moment(reminder.dueAt).locale(activeLocale).format('DD/MM/YYYY')}
                            </span>
                        </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                        <Switch
                            checked={reminder.isActive !== false}
                            onCheckedChange={handleToggleActive}
                            className="data-[state=checked]:border-transparent flex-shrink-0"
                            style={{ background: reminder.isActive !== false ? 'var(--fab-bg)' : undefined }}
                        />

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent/50 flex-shrink-0"
                            onClick={() => handleEdit(reminder)}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                            onClick={() => setIsDeleteDialogOpen(true)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md p-6 sm:p-8">
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            {t('reminders.deleteConfirmation.title', 'Delete reminder?')}
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground">
                            {t(
                                'reminders.deleteConfirmation.description',
                                'Are you sure you want to delete this reminder? This action cannot be undone.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('reminders.deleteConfirmation.cancel', 'Cancel')}
                        </Button>
                        <Button variant="destructive" onClick={() => handleDelete(reminder._id!)}>
                            {t('reminders.deleteConfirmation.confirm', 'Delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default ReminderItem;
